/**
 * Permission codes the rostering screens declare an interest in (design §6.1).
 *
 * `internal/roster` registers all of these; `org.company.read`, `org.unit.read` and
 * `hr.employee.read` are other modules' and are listed because a roster grid cannot do
 * its job without a company, a department and a list of people.
 *
 * **`rosterRead` and `rosterReadOwn` are two codes, not one with a filter.** BUILD_STATE
 * standing rule 9: ask what a permission protects, not who holds it. `roster.roster.read`
 * answers "may you see this company's rosters"; `roster.roster.read.own` answers "may you
 * see a roster you are on". The Employee role holds only the second, and the server scopes
 * the read to a roster containing one of the caller's own lines — 404, never 403.
 *
 * **`rosterPublish` is separate from `rosterManage`** because publish is the action the
 * working-time rules gate. Building a draft that breaches is allowed and useful; sending
 * it to people's phones is the act that is refused.
 */

export const ROSTER_PERMISSIONS = {
  shiftPatternRead: 'roster.shift_pattern.read',
  shiftPatternManage: 'roster.shift_pattern.manage',

  rosterRead: 'roster.roster.read',
  /** Only rosters containing one of the caller's own lines. The Employee role's code. */
  rosterReadOwn: 'roster.roster.read.own',
  rosterManage: 'roster.roster.manage',
  /** The action working-time rules gate. */
  rosterPublish: 'roster.roster.publish',
  rosterLock: 'roster.roster.lock',

  workingTimeRuleRead: 'roster.working_time_rule.read',
  /** House rules only. A `source: 'pack'` rule is read-only to every principal. */
  workingTimeRuleManage: 'roster.working_time_rule.manage',

  demandRead: 'roster.demand.read',
  demandManage: 'roster.demand.manage',
  staffingRuleManage: 'roster.staffing_rule.manage',

  labourCostRead: 'roster.labour_cost.read',

  /* ------------------------------------------------- other modules' codes */

  companyRead: 'org.company.read',
  /** Departments, cost centres and branches — one code, one table. */
  unitRead: 'org.unit.read',
  employeeRead: 'hr.employee.read',
  /** Reviewing an exception means reading and writing attendance. */
  exceptionRead: 'hr.attendance_exception.read',
  exceptionResolve: 'hr.attendance_exception.resolve',
  clockManage: 'hr.clock.manage',
  attendanceRead: 'hr.attendance.read',
} as const

export type RosterPermission = (typeof ROSTER_PERMISSIONS)[keyof typeof ROSTER_PERMISSIONS]
